// RULE 12 — "I looked, and there is nothing to do" is a successful return: the predicate
// that decides it and the refusal shape a manifest carries when a whole assignment was
// declined.
// Split out of `../hive-event.ts`, which is now the barrel over this directory.

import { z } from 'zod';
import { HIVE_EVENT_MAX_ITEMS, line } from './bounds';

/**
 * "I LOOKED, AND THERE IS NOTHING TO DO" — the one return whose ANSWER is empty on
 * purpose (`docs/idea/hive.md` rule 12).
 *
 * A lead asks for work because it believes a defect exists; the belief is the thing under
 * test, and a member that reads the checkout and disproves it has done the job. Both other
 * clauses are required, and each rules out a different way an empty answer would be a lie:
 *
 *   - `falsified` non-empty — otherwise the member is not CONCLUDING anything, it is
 *     simply not answering, and that is `no_answer`/`prose`, which already have kinds.
 *   - `facts` non-empty — every fact carries a `path:line` it opened, so this is the
 *     clause that makes "there is nothing here" a READING rather than an unchecked claim.
 *     Fabricating an absence is the cheapest wrong answer a model can give and nothing
 *     downstream can falsify it.
 *
 * HOISTED HERE FROM THE SCOUT so the two lanes cannot disagree. It was
 * `apps/runner/src/modes/scout/survey.ts`'s private predicate, consulted by the scout
 * finding schema, the survey outcome and the brief renderer; plan 102 slice 02 gives the
 * team lane the same shape, and two copies of this rule is how one lane starts admitting
 * an empty answer the other refuses.
 *
 * Structurally typed rather than taking a named finding type, because the schema that
 * produces that type is the first caller and lives in another package.
 */
export function isNoWorkFinding(finding: {
  readonly answer: string;
  readonly facts: readonly unknown[];
  readonly falsified: readonly unknown[];
}): boolean {
  return finding.answer.trim() === '' && finding.falsified.length > 0 && finding.facts.length > 0;
}

/**
 * A whole assignment refused, measured first (`hive.md` rule 12). Carried by
 * {@link TeamManifestPayloadSchema} as a NULLABLE field: `null` is a coordinator that
 * accepted the work, and the object is one that read the checkout and disproved it.
 *
 * The two `.min(1)` bounds ARE {@link isNoWorkFinding}'s two clauses, stated where a
 * malformed refusal is refused rather than recorded — a `no_work` with an empty
 * `falsified` is a coordinator that failed to answer wearing the costume of one that
 * concluded, and it must not settle a task as `no_work_found`.
 */
export const HiveNoWorkSchema = z.object({
  /** The lead's premises this member disproved. Non-empty, or it concluded nothing. */
  falsified: z
    .array(line())
    .min(1)
    .max(HIVE_EVENT_MAX_ITEMS)
    .describe('hive.md rule 11+12: the premises disproved. Empty is not a refusal.'),
  /** The `path:line`-anchored reading that shows it. Non-empty, or the absence is fabricated. */
  facts: z
    .array(line())
    .min(1)
    .max(HIVE_EVENT_MAX_ITEMS)
    .describe('hive.md rule 12: the anchored reading. Empty makes the absence unfalsifiable.'),
});
export type HiveNoWork = z.infer<typeof HiveNoWorkSchema>;
